import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Search, Eye, Trash2, ShieldCheck, Briefcase, FileText, Wallet } from "lucide-react";

const ROLE_LABEL = { all: "Todos", client: "Cliente", freelancer: "Freelancer", admin: "Admin" };

const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "U";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (role !== "all") params.role = role;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); /* eslint-disable-next-line */ }, [q, role]);

  const openDetail = async (u) => {
    setDetail({ user: u, projects: [], proposals: [], withdrawals: [] });
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}/detail`);
      setDetail(data);
    } catch (e) {
      toast.error("Error al cargar detalle");
    } finally {
      setDetailLoading(false);
    }
  };

  const removeUser = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Usuario eliminado");
      setTimeout(fetchUsers, 250);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o email…"
            className="pl-10 h-11"
            data-testid="admin-users-search"
          />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="h-11 min-w-[180px] w-auto" data-testid="admin-users-role-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ROLE_LABEL).map(([v, l]) => (
              <SelectItem key={v} value={v} data-testid={`admin-users-role-${v}`}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuario</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead className="text-right">Proyectos</TableHead>
                <TableHead className="text-right">Propuestas</TableHead>
                <TableHead className="text-right">Wallet</TableHead>
                <TableHead>Registrado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Cargando…</TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Sin usuarios</TableCell></TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                    <TableCell className="max-w-[300px]">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-9 h-9">
                          <AvatarImage src={u.picture || undefined} />
                          <AvatarFallback className="text-xs">{initials(u.name)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="font-medium truncate flex items-center gap-1.5">
                            {u.name}
                            {u.is_admin && (
                              <ShieldCheck className="w-3.5 h-3.5 text-primary" aria-label="Admin" />
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-[10px] tracking-widest uppercase font-bold px-2 py-1 rounded-sm bg-accent text-accent-foreground">
                        {ROLE_LABEL[u.role] || u.role || "—"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{u.projects_count ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums">{u.proposals_count ?? 0}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">${(u.wallet_balance || 0).toFixed(0)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => openDetail(u)}
                          title="Ver detalle"
                          data-testid={`admin-user-detail-${u.user_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {!u.is_admin && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost" size="icon"
                                className="text-destructive hover:text-destructive"
                                title="Eliminar usuario"
                                data-testid={`admin-user-delete-${u.user_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>¿Eliminar a {u.name}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Esto borrará al usuario y todos sus proyectos, propuestas y empresas asociadas. Acción irreversible.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => removeUser(u.user_id)}
                                  data-testid={`admin-user-confirm-delete-${u.user_id}`}
                                >
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="admin-user-detail-dialog">
          <DialogHeader>
            <DialogTitle>Detalle del usuario</DialogTitle>
            <DialogDescription>Información completa y actividad reciente.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <Avatar className="w-14 h-14">
                  <AvatarImage src={detail.user.picture || undefined} />
                  <AvatarFallback>{initials(detail.user.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
                    {detail.user.name}
                    {detail.user.is_admin && <ShieldCheck className="w-4 h-4 text-primary" />}
                  </div>
                  <div className="text-sm text-muted-foreground">{detail.user.email}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Rol: {ROLE_LABEL[detail.user.role] || detail.user.role} · Registrado {fmtDate(detail.user.created_at)}
                  </div>
                </div>
              </div>

              {detail.user.bio && (
                <div className="text-sm text-muted-foreground border-l-2 border-primary/50 pl-3">
                  {detail.user.bio}
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-md bg-muted/50 border">
                  <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 flex items-center gap-1"><Briefcase className="w-3 h-3" /> Proyectos</div>
                  <div className="text-2xl font-heading font-bold">{detail.projects?.length || 0}</div>
                </div>
                <div className="p-3 rounded-md bg-muted/50 border">
                  <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Propuestas</div>
                  <div className="text-2xl font-heading font-bold">{detail.proposals?.length || 0}</div>
                </div>
                <div className="p-3 rounded-md bg-muted/50 border">
                  <div className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 flex items-center gap-1"><Wallet className="w-3 h-3" /> Wallet</div>
                  <div className="text-2xl font-heading font-bold">${(detail.user.wallet_balance || 0).toFixed(0)}</div>
                </div>
              </div>

              {detailLoading && <div className="text-center text-sm text-muted-foreground">Cargando actividad…</div>}

              {!!detail.projects?.length && (
                <div>
                  <div className="text-xs tracking-widest uppercase font-semibold mb-2">Proyectos publicados ({detail.projects.length})</div>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {detail.projects.map((p) => (
                      <a
                        key={p.project_id}
                        href={`/projects/${p.project_id}`}
                        target="_blank" rel="noreferrer"
                        className="flex justify-between items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm"
                      >
                        <span className="truncate flex-1">{p.title}</span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">${p.budget?.toFixed(0)} · {p.status}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {!!detail.proposals?.length && (
                <div>
                  <div className="text-xs tracking-widest uppercase font-semibold mb-2">Propuestas enviadas ({detail.proposals.length})</div>
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {detail.proposals.slice(0, 50).map((pr) => {
                      const proj = pr.project;
                      const statusColor =
                        pr.status === "accepted" ? "text-emerald-600 bg-emerald-500/10" :
                        pr.status === "rejected" ? "text-destructive bg-destructive/10" :
                        "text-amber-600 bg-amber-500/10";
                      return (
                        <div
                          key={pr.proposal_id}
                          className="p-3 rounded-md border bg-muted/30 text-sm"
                          data-testid={`admin-user-proposal-${pr.proposal_id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              {proj ? (
                                <a
                                  href={`/projects/${proj.project_id}`}
                                  target="_blank" rel="noreferrer"
                                  className="font-medium hover:underline block truncate"
                                  data-testid={`admin-user-proposal-project-${pr.proposal_id}`}
                                >
                                  {proj.title}
                                </a>
                              ) : (
                                <div className="font-medium text-muted-foreground italic">Proyecto eliminado</div>
                              )}
                              {proj && (
                                <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                                  <span>Cliente: <span className="text-foreground">{proj.company_name || proj.owner_name || "—"}</span></span>
                                  <span>·</span>
                                  <span>{proj.category}</span>
                                  <span>·</span>
                                  <span>Presupuesto ${proj.budget?.toFixed(0)}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-1 text-xs whitespace-nowrap">
                              <span className={`px-2 py-0.5 rounded-sm text-[10px] tracking-widest uppercase font-bold ${statusColor}`}>
                                {pr.status === "accepted" ? "Aceptada" : pr.status === "rejected" ? "Rechazada" : "Pendiente"}
                              </span>
                              <span className="tabular-nums font-medium">${pr.amount?.toFixed(0)}</span>
                            </div>
                          </div>
                          {pr.message && (
                            <div className="mt-2 pt-2 border-t text-xs text-muted-foreground line-clamp-2">
                              {pr.message}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
